// Importer les fonctionnalités nécessaires depuis Firestore
import { db, isFirebaseInitialized } from '../firebase'; // Importation de la configuration Firestore depuis le fichier firebase
import {
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore'; // Importation des fonctions Firestore

interface Personne {
  id: string; // ID du document dans Firestore
  name: string; // Nom de la personne
  onContact: boolean; // État de contact (true ou false)
}

// Fonction pour récupérer toutes les personnes dans la collection 'Personnes'
export const getPersonnes = async (): Promise<Personne[]> => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot fetch personnes.');
    return [];
  }
  try {
    // Récupérer tous les documents de la collection 'Personnes'
    const querySnapshot = await getDocs(collection(db!, 'Personnes'));

    // Mapper chaque document pour extraire l'ID, name et onContact
    const personnes = querySnapshot.docs.map((doc) => ({
      id: doc.id, // Ajouter l'ID du document
      name: doc.data().name, // Assurez-vous que le champ name existe
      onContact: doc.data().onContact, // Assurez-vous que le champ onContact existe
    }));

    return personnes; // Retourner la liste des personnes
  } catch (error) {
    // Gérer les erreurs potentielles et afficher un message
    console.error('Erreur lors de la récupération des personnes:', error);
    return []; // Retourner un tableau vide en cas d'erreur
  }
};

// Fonction pour récupérer une personne par son ID
export const getPersonneById = async (id: string) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot fetch personne.');
    return null;
  }
  try {
    // Créer une référence au document correspondant à l'ID donné
    const docRef = doc(db!, 'Personnes', id);

    // Récupérer le document
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // Si le document existe, retourner son ID et ses données
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      // Si le document n'existe pas, afficher un message
      console.log('Aucun document trouvé avec cet ID.');
      return null; // Retourner null si le document n'existe pas
    }
  } catch (error) {
    // Gérer les erreurs potentielles et afficher un message
    console.error('Erreur lors de la récupération de la personne:', error);
    return null; // Retourner null en cas d'erreur
  }
};

// Fonction pour ajouter une nouvelle personne à la collection 'Personnes'
export const addPerson = async (name: string, onContact: boolean) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot add person.');
    return null;
  }
  try {
    // Ajouter un nouveau document à la collection 'Personnes' avec le nom et la date de création
    const docRef = await addDoc(collection(db!, 'Personnes'), {
      name,
      onContact, // Ajouter le champ onContact
      createdAt: new Date(), // Ajouter une date de création
    });

    console.log("Document ajouté avec l'ID:", docRef.id); // Afficher l'ID du nouveau document
    return docRef.id; // Retourner l'ID du document ajouté
  } catch (error) {
    // Gérer les erreurs potentielles et afficher un message
    console.error("Erreur lors de l'ajout du document:", error);
  }
};

// Fonction pour supprimer une personne par son ID
export const removePerson = async (personId: string) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot remove person.');
    return;
  }
  try {
    // Créer une référence au document à supprimer
    const personRef = doc(db!, 'Personnes', personId);

    // Supprimer le document de Firestore
    await deleteDoc(personRef);

    console.log(`Personne avec ID ${personId} supprimée avec succès.`); // Afficher un message de succès
  } catch (error) {
    // Gérer les erreurs potentielles et afficher un message
    console.error('Erreur lors de la suppression de la personne:', error);
  }
};

// Fonction pour ajouter un nouvel article à la collection 'Articles'
export const addArticle = async (
  title: string,
  markdownContent: string,
  personneId: string
) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot add article.');
    return null;
  }
  try {
    // Ajouter un nouveau document à la collection 'Articles' avec les champs spécifiés
    const docRef = await addDoc(collection(db!, 'Articles'), {
      title,
      markdownContent,
      personneId,
      createdAt: new Date(), // Ajouter une date de création
    });

    console.log("Document ajouté avec l'ID:", docRef.id); // Afficher l'ID du nouveau document
    return docRef.id; // Retourner l'ID du document ajouté
  } catch (error) {
    console.error("Erreur lors de l'ajout de l'article:", error);
  }
};

// Fonction pour supprimer un article par son ID
export const removeArticle = async (articleId: string) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot remove article.');
    return;
  }
  try {
    // Créer une référence au document à supprimer
    const articleRef = doc(db!, 'Articles', articleId);

    // Supprimer le document de Firestore
    await deleteDoc(articleRef);

    console.log(`Article avec ID ${articleId} supprimé avec succès.`);
  } catch (error) {
    console.error("Erreur lors de la suppression de l'article:", error);
  }
};

// Fonction pour mettre à jour un article par son ID
export const updateArticle = async (
  articleId: string,
  updatedData: Partial<{
    title: string;
    markdownContent: string;
    personneId: string;
  }>
) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot update article.');
    return;
  }
  try {
    // Créer une référence au document à mettre à jour
    const articleRef = doc(db!, 'Articles', articleId);

    // Mettre à jour le document avec les nouvelles données
    await updateDoc(articleRef, updatedData);

    console.log(`Article avec ID ${articleId} mis à jour avec succès.`);
  } catch (error) {
    console.error("Erreur lors de la mise à jour de l'article:", error);
  }
};

// Fonction pour récupérer tous les articles d'un utilisateur spécifique
export const getArticlesByPersonneId = async (personneId: string) => {
  if (!isFirebaseInitialized()) {
    console.warn('Firebase is not initialized. Cannot fetch articles.');
    return [];
  }
  try {
    // Créer une requête pour filtrer les articles par personneId
    const articlesQuery = query(
      collection(db!, 'Articles'),
      where('personneId', '==', personneId)
    );

    // Récupérer les documents correspondant à la requête
    const querySnapshot = await getDocs(articlesQuery);

    // Créer un tableau pour stocker les articles récupérés
    const articles: any[] = [];

    // Parcourir les résultats et ajouter chaque article au tableau
    querySnapshot.forEach((doc) => {
      articles.push({ id: doc.id, ...doc.data() }); // Ajouter l'ID du document et les données
    });

    return articles; // Retourner le tableau des articles
  } catch (error) {
    console.error('Erreur lors de la récupération des articles:', error);
    return []; // Retourner un tableau vide en cas d'erreur
  }
};
